import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { OperationActivityFilters } from '@/api/models';
import type { GetOperationActivityResponse } from '@/api/handlers/resources/operations/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { OperationsQueryKeys } from './operationsQueryKeys';

const STALE_TIME_MS = 30_000;

/**
 * A page of recorded operations.
 *
 * `placeholderData: keepPreviousData` — unlike the catalog's, which has none.
 * This list *is* paged and filtered, so there is always a previous answer, and
 * holding it on screen while the next one loads is what stops the register
 * collapsing to an empty box between page 1 and page 2. The table's own
 * `isLoading` still distinguishes "not known yet" from "none".
 *
 * `enabled` is the caller's, the pattern every accounting query here follows:
 * access is derived from the session before fetching, because a raw 403 tells
 * the user nothing they can act on.
 */
export const useOperationActivityQuery = (
    filters: OperationActivityFilters,
    enabled: boolean,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetOperationActivityResponse>>({
        queryKey: OperationsQueryKeys.activity(filters),
        queryFn: () => apiClient.operations.getActivity(filters),
        enabled,
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
    });
};
